/**
 * Admin parent layout route — matches /admin (prefix).
 *
 * Gate:
 *   1. While AuthProvider is hydrating (`loading: true`) — render nothing.
 *      Flashing a /auth/login redirect during hydration causes a bounce
 *      loop the moment the real session arrives.
 *   2. No session → /auth/login.
 *   3. Role not in the admin-allowed set → /app  (NOT 403 / "not authorized"
 *      / sign-out — we silently redirect any authenticated-but-wrong-role
 *      user back to the regular product surface, mirroring the
 *      "don't leak that /superadmin exists" posture from REQ-AUTH-015).
 *   4. FIRM_STAFF with NO verified TOTP factor → /auth/mfa-enroll (W10, below),
 *      unless the SERVER vouches for it as a step-up test account (Addendum 6,
 *      3 September 2026, Paul's pick A). That vouch is a 200 from
 *      POST /v1/auth/refresh-step-up, which only a user id on the dev
 *      STEP_UP_TEST_USER_IDS allowlist can get without an MFA challenge, and it
 *      satisfies the AAL-2 gate below as well. Unset in production, where the
 *      call answers 403 for everyone, so the exception cannot exist there. See
 *      lib/auth-server-vouch.ts and ServerVouchState below.
 *   5. Otherwise → <Outlet />.
 *
 * Allowed roles include both spellings of "super admin" because the JWT
 * may carry either `super_admin` (canonical, set by the onboarding FA per
 * backend/handlers/superadmin-bootstrap.ts) or `superadmin` (legacy from a
 * pre-snake_case migration window) — accepting both is cheap and avoids a
 * hard cut-over.
 *
 * Role source is derived from the session via the AuthContext (Phase 10b)
 * — see lib/auth-context.tsx `deriveRole()`. Individual admin routes
 * remain free to do finer-grained checks (e.g. compliance-only billing
 * adjustments) on top of this gate.
 *
 * W10 — staff-MFA enrolment gate (2026-07-11, Paul's ruling: "invited staff
 * MUST get MFA like the founder does"). The founder is hard-gated through MFA
 * enrolment DURING firm onboarding (`onboarding/firm-screen.tsx`'s `mfa` phase)
 * before ever reaching /admin — but an invited teammate used to set a password
 * (reset-password.tsx) and land straight in the console with NO factor,
 * forever password-only; step-up catches WRITES (server-side aal2 checks) but
 * every READ stayed open at aal1. This gate closes that for every visit to
 * /admin, not just the first: it checks whether the signed-in FIRM_STAFF
 * session has a VERIFIED TOTP factor at all (same
 * `(data?.totp ?? []).some(f => f.status === 'verified')` read
 * `onboarding/firm-screen.tsx`'s resume gate uses) and, if not, redirects to
 * the existing enrolment surface with a return trip back to /admin/dashboard.
 *
 * This is deliberately a DIFFERENT check from /app's AAL gate (app/_layout.tsx):
 * that one asks "has THIS SESSION stepped up to aal2" (a factor exists but
 * wasn't challenged this sign-in) and bounces to /auth/mfa-verify — a re-
 * challenge screen. This gate asks "does a factor exist AT ALL" and bounces to
 * /auth/mfa-enroll — an ENROL screen. A factor-enrolled operator normally never
 * sees this path: item 3 below already routes a password login for a factor-
 * enrolled user through the mfa-verify challenge before /admin ever mounts, so
 * by the time this gate runs they are aal2 with a verified factor on file.
 *
 * FAIL-OPEN on a transient listFactors() error (unlike /app's fail-closed AAL
 * check): unlike mfa-verify (a harmless re-prompt), /auth/mfa-enroll calls
 * `mfa.enroll()` unconditionally on mount — bouncing an ALREADY-enrolled
 * founder/operator there on a network blip risks a real dead-end
 * (`mfa_factor_name_conflict` against their own verified factor, since
 * mfa-enroll.tsx enrols with no unique friendly name — see lib/mfa-totp.ts's
 * documented history of that exact failure mode). This screen is UX / defence-
 * in-depth (route-policy.ts header) on top of the real server-side step-up
 * boundary, so erring toward "render the console" on a transient check failure
 * is the safer failure direction here, not the AAL gate's "block the surface".
 */

import { useEffect, useState } from 'react';
import { createFileRoute, Navigate, Outlet, useLocation } from '@tanstack/react-router';
import { useSession } from '../../lib/use-session';
import { clearActAsContext } from '../../lib/act-as-context';
import { forgetLastKnownActAsSubject } from '../../lib/acting/context';
import { canAccess } from '../../lib/authz/route-policy';
import { supabase } from '../../lib/supabase';
import {
  accessTokenClaims, readStepUpStampSeconds, serverVouchForTestAccount,
} from '../../lib/auth-server-vouch';
import { AdminNotFound } from '../../components/error-pages/admin-not-found';
import { NotAvailableToRole } from '../../components/admin/not-available-to-role';
import { adminRouteAccess } from '../../lib/authz/admin-route-access';

export const Route = createFileRoute('/admin')({
  // S-1.3: entering the firm console drops any act-as / impersonation context —
  // you cannot be "administering a client" while standing in the firm console.
  // Runs before the component (and its identity foot) render, so the console can
  // never read a lingering client token even on a direct nav from an act-as
  // portal (this is why the console foot itself needs no operator-pin repoint).
  beforeLoad: () => {
    clearActAsContext();
    // AND FORGET WHO IT WAS (ruling 6, 2026-09-03). Entering the console is a
    // DELIBERATE exit, and `lib/acting/last-act-as.ts` says in its header that a
    // deliberate exit is the only place this call belongs: it must never run on
    // an expiry, because the sticky subject is exactly what an expiry needs.
    //
    // It matters now because /app's guard reads that fact to tell "your 15
    // minutes ran out" (show the re-enter screen) apart from "you left on
    // purpose" (bounce to the console). Without this call, a firm operator who
    // pressed "Exit to clients" and then typed an /app URL would be offered a
    // client they had already stepped out of.
    forgetLastKnownActAsSubject();
  },
  component: AdminRoot,
  // Mistyped /admin/* URLs get the branded console 404, not the router's bare
  // "Not Found" (firm-rehearsal 2026-07-11, P3 #27).
  notFoundComponent: () => <AdminNotFound />,
});

type StaffMfaState =
  | { status: 'checking' }
  | { status: 'has-factor' }
  | { status: 'no-factor' };

/**
 * AAL-2 (2026-07-30) — has THIS SESSION passed the second factor?
 *
 * `StaffMfaState` above answers only "is a factor ENROLLED", which is a
 * different question. Until this state existed the console rendered for anyone
 * with a factor on file, so a firm admin who signed in with a password ALONE
 * reached the whole of /admin/* at aal1. Reproduced on PROD 2026-07-30 with a
 * real sign-in: token claims `aal: aal1`, `amr: [password]`, and
 * /admin/dashboard rendered. Sensitive WRITES were still blocked server-side
 * (step-up re-checks aal2), so this was read-exposure of the firm console —
 * client book, trust dossiers, firm data — to a bare password. Firm-staff MFA
 * is MANDATORY (W10), which is exactly what that hole undercut.
 *
 * Mirrors the /app gate (routes/app/_layout.tsx): the SAME
 * `currentLevel`/`nextLevel` signal, and it fails CLOSED — a probe error sends
 * the operator to /auth/mfa-verify rather than silently dropping the gate.
 * (The enrolment check above deliberately fails OPEN for a different reason —
 * see the file header. The two directions are intentional, not inconsistent:
 * never strand an enrolled operator on an enrol screen, but never render the
 * console on an unproven session either.)
 *
 * /auth/mfa-verify routes by role on success (`landingRouteForRole`), so a
 * verified firm operator lands back in their console with no `next` plumbing.
 */
type SessionAalState =
  | { status: 'checking' }
  | { status: 'stepped-up' }
  | { status: 'needs-step-up' };

/**
 * SERVER VOUCH (Paul's ruling on Addendum 6, 3 September 2026, pick A).
 *
 * The no-code walk bot is a staff account with NO second factor on purpose, so
 * the two gates above shut the firm console to it and the whole firm surface has
 * only ever been walked through the API, never in a browser. This is the one
 * exception, and it is the server's to grant: `lib/auth-server-vouch.ts` asks
 * `POST /v1/auth/refresh-step-up`, which answers 200 without an MFA challenge
 * ONLY for a user id named in `STEP_UP_TEST_USER_IDS` on the dev onboarding
 * Function App. That setting is unset in production and is kept out of every
 * production config by `backend/scripts/lint-step-up-test-accounts.mjs`, so on
 * prod this can only ever resolve 'refused'. Absence IS the closed state; there
 * is no environment predicate here to get wrong.
 *
 * 'idle' is the state of every session that must never be asked: anyone who is
 * not firm staff, and any staff member who HAS a verified factor. Their paths
 * through both gates are untouched.
 *
 * The one visible change for a real staff member with no factor: the enrolment
 * bounce now waits for a single refused round trip, rendering nothing meanwhile,
 * the same nothing this screen already renders while `staffMfa` is 'checking'.
 * It cannot be avoided by bouncing first and asking later, because the redirect
 * unmounts the layout. The destination is unchanged.
 */
type ServerVouchState =
  | { status: 'idle' }
  | { status: 'asking' }
  | { status: 'vouched' }
  | { status: 'refused' };

function AdminRoot() {
  const { session, role, loading } = useSession();
  const pathname = useLocation({ select: (l) => l.pathname });
  // WHERE TO COME BACK TO. The enrol bounce used to hardcode /admin/dashboard,
  // so an operator sent to enrol from /admin/billing?paystack_callback=1, on
  // the way back from paying, lost both the screen and the card-saved marker
  // the backend had just put on its own return URL. The query string is part of
  // the destination, not decoration (NEW-PAYSTACK-RETURN-ENROL, 22 Sep 2026).
  const returnTo = useLocation({
    select: (l) => `${l.pathname}${(l as { searchStr?: string }).searchStr ?? ''}`,
  });
  const [staffMfa, setStaffMfa] = useState<StaffMfaState>({ status: 'checking' });
  const [sessionAal, setSessionAal] = useState<SessionAalState>({ status: 'checking' });
  const [vouch, setVouch] = useState<ServerVouchState>({ status: 'idle' });

  // W10: look up the signed-in session's TOTP enrolment. Runs for every
  // session (cheap, cached by supabase-js) — the role check below decides
  // whether the result actually gates anything.
  useEffect(() => {
    if (loading || !session) {
      setStaffMfa({ status: 'checking' });
      return;
    }
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (cancelled) return;
      if (error) {
        // Fail OPEN — see file header. A transient check failure must not risk
        // bouncing an already-enrolled operator into a dead-end enrol screen.
        setStaffMfa({ status: 'has-factor' });
        return;
      }
      const hasVerifiedTotp = (data?.totp ?? []).some((f) => f.status === 'verified');
      setStaffMfa({ status: hasVerifiedTotp ? 'has-factor' : 'no-factor' });
    })();
    return () => {
      cancelled = true;
    };
  }, [session, loading]);

  // AAL-2: does the CURRENT session owe an MFA promotion? Fails CLOSED.
  useEffect(() => {
    if (loading || !session) {
      setSessionAal({ status: 'checking' });
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (cancelled) return;
        if (error || !data) {
          setSessionAal({ status: 'needs-step-up' });
          return;
        }
        const needsStepUp = data.currentLevel === 'aal1' && data.nextLevel === 'aal2';
        setSessionAal({ status: needsStepUp ? 'needs-step-up' : 'stepped-up' });
      } catch {
        if (!cancelled) setSessionAal({ status: 'needs-step-up' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session, loading]);

  // SERVER VOUCH: asked ONLY for a staff session that has no verified factor,
  // which is the exact shape both gates refuse. A session with a factor never
  // reaches this call, and neither does a non-staff session: `canAccess` is the
  // same allow-set the render path uses two screens down, so the two cannot
  // drift. `serverVouchForTestAccount` caches its answer per user id, so this
  // effect re-running (the vouch itself refreshes the session on success, which
  // hands us a new session object) never asks twice.
  const userId = session?.user?.id;
  const isStaffRole = canAccess('admin', role);
  // The step-up stamp this session's ACCESS TOKEN carries, if any (the token
  // is what the server reads; QA P2-9). Passed to the vouch so an
  // already-stamped session is answered without a round trip; depended on as a
  // NUMBER so a new session object with the same stamp does not re-run the
  // effect. Whether the stamp is bound to THIS session is decided inside the
  // vouch, by the same rule the server applies.
  const accessToken = session?.access_token;
  const stampedAtSeconds = readStepUpStampSeconds(accessTokenClaims(accessToken)?.['app_metadata']);
  useEffect(() => {
    if (loading || !userId || !isStaffRole || staffMfa.status !== 'no-factor') return;
    let cancelled = false;
    // Sticky once earned: a re-run must not drop a vouched session back through
    // 'asking' and blank the console for a tick.
    setVouch((prev) => (prev.status === 'vouched' ? prev : { status: 'asking' }));
    void (async () => {
      const ok = await serverVouchForTestAccount(userId, accessToken);
      if (cancelled) return;
      setVouch({ status: ok ? 'vouched' : 'refused' });
    })();
    return () => {
      cancelled = true;
    };
    // `accessToken` changes on every session refresh; the only part of it this
    // call reads is the stamp, so that is the dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, loading, isStaffRole, staffMfa.status, stampedAtSeconds]);

  if (loading) return null;

  if (!session) {
    return <Navigate to="/auth/login" />;
  }

  // S-2.1: allow-set sourced from the central route-policy (firm staff). The
  // super_admin/superadmin spelling drift is resolved there via normalizeRole.
  if (!canAccess('admin', role)) {
    return <Navigate to="/app" />;
  }

  // W10: every FIRM_STAFF role that reaches this point (canAccess('admin', …)
  // above already narrowed `role` to the FIRM_STAFF set) must have a verified
  // second factor on file. A founder mid-onboarding never sees this — they are
  // hard-gated through MFA before /onboarding/firm ever hands off to /admin —
  // and a super_admin never sees it either (REQ-AUTH-015 already demands a
  // fresh aal2 stamp to reach /superadmin, so their factor predates this
  // check). It exists for the one gap those two flows don't cover: an invited
  // teammate who set a password and never enrolled.
  if (staffMfa.status === 'checking') return null;
  if (staffMfa.status === 'no-factor' && vouch.status !== 'vouched') {
    // The vouch is in flight (or has not been kicked off by the effect yet, since
    // effects run after the commit that first reports 'no-factor'). Hold, exactly
    // as this screen holds while the factor lookup runs: bouncing now would
    // unmount the layout and the answer would never arrive. A session carrying no
    // user id can never be asked, so it bounces immediately as it always did, and
    // the vouch itself gives up after 5 seconds rather than holding a real
    // operator on a blank screen.
    if (userId && vouch.status !== 'refused') return null;
    return <Navigate to="/auth/mfa-enroll" search={{ next: returnTo } as never} />;
  }

  // AAL-2: a factor is on file — but this SESSION must have passed it before any
  // console surface renders. Enrolment is checked first so an unenrolled operator
  // still lands on /auth/mfa-enroll (W10) rather than a verify screen with
  // nothing to verify against.
  if (sessionAal.status === 'checking') return null;
  if (sessionAal.status === 'needs-step-up' && vouch.status !== 'vouched') {
    return <Navigate to="/auth/mfa-verify" />;
  }

  // Bare /admin has no index route — a manual URL / bookmark to it would render a
  // blank layout shell (found by the 2026-07-01 render sweep). Send it to the
  // dashboard, mirroring how /app self-redirects bare /app → /app/dashboard.
  if (pathname === '/admin' || pathname === '/admin/') {
    return <Navigate to="/admin/dashboard" />;
  }

  // A PAGE THE ROLE CANNOT USE (round 11, round 10). Everything above admits
  // any firm-staff role to any /admin path, and until now the sidebar was the
  // only thing between a relationship manager and the pages the server refuses
  // it: typed, /admin/billing mounted and every read on it answered 403. The
  // decision is the server's tier for that page's own reads
  // (lib/authz/admin-route-access.ts), made here so the page never mounts and
  // never asks.
  const access = adminRouteAccess(pathname, role);
  if (!access.allowed) {
    return <NotAvailableToRole page={access.name} />;
  }

  return <Outlet />;
}

export default AdminRoot;
